import {useMemo} from 'react';

import {
  ApiRuleBundlesSearch,
  RuleBundleModel,
  RuleBundleModelStrict,
  RuleBundleSearchModel,
} from '../../models/api/ruleBundleModel';
import {makeResourceListPaginationSearchOffset} from '../helpers/resourceListPagination/resourceListPaginationSearchOffset';
import {usePersistedListMutation, useResourceList} from '../helpers/resourceTableHooks';
import {SettingsRuleListFiltersState} from '../rules/list/ruleBundleCategory';
import {buildRuleSearchUrl, RuleListTableSortsEnum} from '../rules/list/ruleListHelpers';
import {useQueryDebounce} from './fetcherHelpers';
import {RuleListData, RuleListMutationAction, ruleListMutations} from './rulesBundlesMutations';

/*
 * Consts.
 */

const ruleBundleQueryKeyFactory = {
  // You can use this to fuzzy-select all rule bundles queries.
  all: ['ruleBundles'],
  list: (
    namespace: string,
    filters: SettingsRuleListFiltersState,
    ruleSort: RuleListTableSortsEnum | undefined,
  ) => [...ruleBundleQueryKeyFactory.all, {namespace, filters, ruleSort}],
};

/*
 * Fetchers.
 */

export function useFetchRuleBundles(
  namespace: string,
  filters: SettingsRuleListFiltersState,
  ruleSort: RuleListTableSortsEnum,
) {
  const {query, ...otherFilters} = filters;

  const debouncedQuery = useQueryDebounce(query);

  const debouncedFilters = useMemo(
    () => ({...otherFilters, query: debouncedQuery}),
    [debouncedQuery, otherFilters],
  );

  const queryKey = ruleBundleQueryKeyFactory.list(namespace, debouncedFilters, ruleSort);

  const ruleSearchPagination = useMemo(
    () =>
      makeResourceListPaginationSearchOffset<RuleBundleModel, RuleBundleSearchModel>({
        apiType: ApiRuleBundlesSearch,
        makeSearchUrl: (page, company) =>
          buildRuleSearchUrl(company, namespace, debouncedFilters, page, ruleSort),
      }),
    [debouncedFilters, namespace, ruleSort],
  );

  const {items, loadMore, moreStatus, showErrorBanner} = useResourceList(queryKey, ruleSearchPagination);

  const {mutate} = usePersistedListMutation<
    RuleListMutationAction,
    RuleListData,
    typeof ruleListMutations,
    Error
  >(ruleBundleQueryKeyFactory.all, ruleListMutations, showErrorBanner, true);

  return {
    items: items as ReadonlyArray<RuleBundleModelStrict>,
    loadMore,
    moreStatus,
    mutate,
  };
}
